import type { SkuComponentType } from "./productFormUtils";

export type SkuComponentForm = {
  componentType: SkuComponentType;
  displayName: string;
  quantity: string;
  comment: string;
};

type IndexedComponent = {
  component: SkuComponentForm;
  index: number;
};

type SkuComponentsEditorProps = {
  type: SkuComponentType;
  title: string;
  description: string;
  icon: string;
  components: IndexedComponent[];
  onToggle: (enabled: boolean) => void;
  onAdd: () => void;
  onUpdate: (componentIndex: number, patch: Partial<SkuComponentForm>) => void;
  onRemove: (componentIndex: number) => void;
};

export default function SkuComponentsEditor({
  type,
  title,
  description,
  icon,
  components,
  onToggle,
  onAdd,
  onUpdate,
  onRemove,
}: SkuComponentsEditorProps) {
  const enabled = components.length > 0;
  const isSetComposition = type === "set_item";
  const accentClass = isSetComposition
    ? "border-amber-200 bg-amber-50/60"
    : "border-emerald-200 bg-emerald-50/50";
  const iconClass = isSetComposition
    ? "bg-amber-100 text-amber-700"
    : "bg-emerald-100 text-emerald-700";

  return (
    <div className={`rounded-xl border p-3.5 transition-colors ${enabled ? accentClass : "border-[#E8E2D8] bg-[#FCFAF7]"}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconClass}`}>
          <i className={icon} />
        </span>
        <div className="min-w-0 flex-1">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-800">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => onToggle(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            {title}
          </label>
          <p className="mt-1 text-xs leading-5 text-gray-500">{description}</p>
        </div>
        {enabled ? (
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[#D8D1C7] bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:border-amber-300 hover:text-amber-700"
          >
            <i className="ri-add-line" />
            Добавить строку
          </button>
        ) : null}
      </div>

      {enabled ? (
        <div className="mt-3 space-y-2.5 border-t border-black/5 pt-3">
          {components.map(({ component, index }, visibleIndex) => (
            <div key={index} className="rounded-lg border border-white/80 bg-white p-3 shadow-sm">
              <div className="mb-2.5 flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500">
                  {isSetComposition ? "Позиция" : "Подарок"} {visibleIndex + 1}
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="ml-auto text-xs text-red-500 hover:text-red-600"
                >
                  Удалить
                </button>
              </div>
              <input
                value={component.displayName}
                onChange={(event) => onUpdate(index, { displayName: event.target.value })}
                className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                placeholder={isSetComposition ? "Например: Акациевый мёд, банка 0,5 кг" : "Например: Подарочная открытка"}
              />
              <div className="mt-2.5 grid grid-cols-1 gap-2.5 md:grid-cols-[120px_1fr]">
                <input
                  value={component.quantity}
                  onChange={(event) => onUpdate(index, { quantity: event.target.value })}
                  type="number"
                  min={1}
                  className="rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  placeholder="Кол-во"
                />
                <input
                  value={component.comment}
                  onChange={(event) => onUpdate(index, { comment: event.target.value })}
                  className="rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  placeholder={isSetComposition ? "Фасовка или пояснение" : "Комментарий для комплектации"}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
